import { DestroyRef, EventEmitter, Injectable } from '@angular/core';
import { ParamMap, Router } from '@angular/router';
import {
  CartInterface,
  CartEventType,
  BaseCartEvent,
  CartEventProductRemoval,
  CartEventProductUpdated,
  RegisterData
} from './cart.interface';
import { Person } from '../entities/person';
import { Cart } from '../entities/cart';
import { Shop, UnbookableReason, unbookableType } from '../../../../shared/entities/shop';
import { AgeIntervalInterface } from '../../../../shared/interfaces/age-interval-interface';
import { BookingParameterService } from '../../../../core/services/booking-parameter.service';
import { Addon, Product } from '../entities';
import { Locale, SupportedCurrency } from '@frontend';
import {
  catchError,
  debounceTime,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  Subject,
  switchMap,
  take
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { delayedRetry } from '../../../../../../projects/platform-manager/src/lib/services/DEFAULT_MAX_RETRIES';
import { codetype } from '../../../../shared/entities/bookingParameters';
import { AmountInterface } from '../../../../layout/interfaces/amount-interface';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { PlatformInformationService } from '../../../../../../projects/platform-manager/src/public_api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpHeaders } from '@angular/common/http';
import { NotificationService } from '../../../../shared/services/notification.service';
import { DatePipe } from '@angular/common';
import { PartnerLinkService } from '../../../../core/services/partner-link.service';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import {
  CoreService,
  LegacyAccessoryDto,
  LegacyDynamicPricesRequestDto,
  LegacyDynamicPricesResponseDto,
  LegacyEquipmentDto,
  LegacyInsuranceDto,
  LegacyPersonDto
} from '../../generated';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  public cart = new Cart(); //: Cart;
  public cart$ = new ReplaySubject<
    BaseCartEvent | CartEventProductRemoval | CartEventProductUpdated // TODO: add more
  >(1);
  protected defaultAgeAdult = 36;
  public getProductsWithDiscounts$ = new Subject<Cart>(); // trigger discount fetch from outer components
  public latestProductsWithDiscounts$ = this.getProductsWithDiscounts$.pipe(
    debounceTime(500),
    switchMap(cart => {
      if (this.platformInformationService.getIsBrowser()) {
        return this.fetchDiscountsFromCore(
          this.translateService.currentLang as Locale
          // this.currencyService.actualCurrency as Currency - if we ever want make the system worse, switch to non-euro based calculations
        );
      } else {
        // SSR should never call the discount api since it depends on user input and location and request date and can not be cached.
        return of(cart);
      }
    })
  );

  private cartWorker?: Worker;

  constructor(
    private translateService: TranslateService,
    private bookingParameterService: BookingParameterService,
    private platformService: PlatformInformationService,
    private cookieService: SsrCookieService,
    private coreService: CoreService,
    private platformInformationService: PlatformInformationService,
    private notificationService: NotificationService,
    private datePipe: DatePipe,
    private partnerLinkService: PartnerLinkService,
    private localizeRouterService: LocalizeRouterService,
    private router: Router
  ) {
    if (this.platformInformationService.getIsBrowser()) {
      this.cartWorker = new Worker(new URL('../../../../cart-idb.worker.ts', import.meta.url));
      this.cartWorker.onmessage = (event: MessageEvent<{ eventType: string; cartFromIDB: Cart }>) => {
        if (event.data.eventType === 'FETCHED_FROM_IDB' && event.data.cartFromIDB) {
          this.cart.registerData = event.data.cartFromIDB.registerData;
          this.cart.persons.forEach((p, index) => {
            const IDBPerson = event.data.cartFromIDB.persons[index];
            if (IDBPerson) {
              p.name = IDBPerson.name;
              p.birthday = IDBPerson.birthday;
              p.weight = IDBPerson.weight;
              p.height = IDBPerson.height;
              p.shoe_size = IDBPerson.shoe_size;
              p.skill = IDBPerson.skill;
            }
          });
          this.updateCart(CartEventType.cartInitialisedByIDB);
        }
      };
    }
  }

  /**
   * In case we can not serve an available product selection for the given search params,
   * we redirect the customers to the townpage to make an alternative choice. To hit the SSR cache,
   * we strip the search params and cart since the data is stored in the customers booking params (localStorage)
   * @param shop
   */
  private navigateToTownPage(shop: Shop) {
    const route = this.partnerLinkService
      .getUrlReplacingPartnerAfterPrefix(
        this.localizeRouterService.translateRoute(
          '/SKIHIREENTRY/' + shop.townSlugs[this.translateService.currentLang]
        ),
        this.localizeRouterService.translateRoute('/SKIHIREENTRY')
      )
      .replace('partner-', 'partner-/');
    this.router.navigate([route], {
      queryParams: {
        cart: null,
        startDate: null,
        endDate: null
      }
    });
  }

  private handleUnbookableReasons(unbookableReasons: UnbookableReason[], shop: Shop) {
    let transkey = '';
    let transvars = {};
    const dateFormat = this.translateService.instant('CORE.notificationDateFormat');

    switch (unbookableReasons[0].type) {
      case unbookableType.minDuration:
        transkey = 'NOTIFICATION.shopMinDuration';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          min_duration: unbookableReasons[0].minDuration
        };
        break;
      case unbookableType.maxDuration:
        transkey = 'NOTIFICATION.shopMaxDuration';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          max_duration: unbookableReasons[0].maxDuration
        };
        break;
      case unbookableType.minDelay:
        transkey = 'NOTIFICATION.shopMinDelay';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          min_delay: unbookableReasons[0].minDelay
        };
        break;
      case unbookableType.after:
        transkey = 'NOTIFICATION.shopAfterSeason';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          end_date: this.datePipe.transform(unbookableReasons[0].end, dateFormat, undefined, 'en-US')
        };
        break;
      case unbookableType.before:
        transkey = 'NOTIFICATION.shopBeforeSeason';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          start_date: this.datePipe.transform(unbookableReasons[0].start, dateFormat, undefined, 'en-US')
        };
        break;
      case unbookableType.pause:
        transkey = 'NOTIFICATION.shopPause';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          start_date: this.datePipe.transform(unbookableReasons[0].start, dateFormat, undefined, 'en-US'),
          end_date: this.datePipe.transform(unbookableReasons[0].end, dateFormat, undefined, 'en-US')
        };
        break;
      case unbookableType.unservableAges:
        transkey = 'NOTIFICATION.shopUnservableAges';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          age: unbookableReasons[0].age
        };
        break;
    }

    if (transkey) {
      const confirmEmitter = new EventEmitter<null>();
      confirmEmitter.pipe(take(1)).subscribe(() => {
        this.navigateToTownPage(shop);
      });

      this.notificationService.add({
        displayTime: 0,
        transkey,
        transvars,
        confirmation: [
          {
            type: 'primary',
            confirmationTrans: 'CORE.notification.ok',
            confirmationEmitter: confirmEmitter
          }
        ],
        id: 'blocker'
      });
    }
  }

  /**
   * Checks the query parameters passed, and if they are valid it will create a cart based on those
   * Otherwise, it will fallback to the default cart from bookingParams
   * Later on it will also check the IndexedDb to see if we can get something from there
   */
  public initCart(params: ParamMap, shop: Shop, products: Product[], destroyRef?: DestroyRef) {
    this.cart.mapDataFrom(this.sanitizeParams(params, shop, products), shop, products);
    if (this.platformInformationService.getIsBrowser()) {
      const unbookableReasons = shop.unbookableReasonsFor(
        this.cart.startDate,
        this.cart.endDate,
        this.cart.persons.map(p => p.age)
      );
      if (unbookableReasons.length) {
        this.handleUnbookableReasons(unbookableReasons, shop);
      }
      this.cartWorker?.postMessage({ eventType: 'GET', shopId: this.cart.shop.id.toString() });
    }
    if (destroyRef) {
      const eventMeaningfulForIDBUpdate = [
        CartEventType.cartInitialised,
        CartEventType.couponcodeUpdated,
        CartEventType.dateRangeUpdated,
        CartEventType.personAdded,
        CartEventType.personAgeUpdated,
        CartEventType.personNameUpdated,
        CartEventType.personRemoved,
        CartEventType.productRemoved,
        CartEventType.registerDataUpdated,
        CartEventType.productUpdated,
        CartEventType.promocodeUpdated
      ];
      this.cart$
        .pipe(
          filter(ev => eventMeaningfulForIDBUpdate.includes(ev.eventType)),
          debounceTime(500),
          takeUntilDestroyed(destroyRef)
        )
        .subscribe(() => {
          this.cartWorker?.postMessage({ eventType: 'PUT', cart: this.cart });
        });
    }
    this.updateCart(CartEventType.cartInitialised);
  }

  /**
   * sets selected person and fires event.
   * Will only send event if the selectedPerson changes
   * @param index
   */
  public selectPerson(index: number) {
    if (
      index >= 0 && // not negative
      index <= this.cart.persons.length && // within range
      this.cart.persons.indexOf(this.cart.selectedPerson) !== index // not the same
    ) {
      this.cart.selectedPerson = this.cart.persons[index];
      this.updateCart(CartEventType.personSelected);
    }
  }

  /**
   * It will add a new person if index not provided
   */
  public addPerson(shop: Shop) {
    const person = new Person();
    person.ageInterval = shop.ageIntervals.find(ai => ai.is_default);
    person.age = person.ageInterval.start === 0 ? person.ageInterval.end : person.ageInterval.start;
    this.cart.persons.push(person);
    this.cart.selectedPerson = person;
    this.updateCart(CartEventType.personAdded);
  }

  public updateRegisterData(registerData: RegisterData) {
    this.cart.registerData = registerData;
    this.updateCart(CartEventType.registerDataUpdated);
  }

  public copyPastePerson(person: Person) {
    const newPerson = new Person();
    newPerson.ageInterval = person.ageInterval;
    newPerson.age = person.age;
    person.productEntities.forEach(productToCopy => {
      newPerson.productEntities.push(new Product(productToCopy));
    });
    this.cart.persons.push(newPerson);
    this.cart.selectedPerson = person;

    this.updateCart(CartEventType.personAdded);
  }

  /**
   * It will update the age of the person at given index
   */
  public updatePersonAge(ageInterval: AgeIntervalInterface, index: number) {
    const person = this.cart.persons[index];
    person.age = ageInterval.start !== 0 ? ageInterval.start : ageInterval.end;
    person.ageInterval = ageInterval;
    // we need to drop equipment that is unfit for the new ageInterval
    for (let i = person.productEntities.length - 1; i >= 0; i--) {
      if (person.productEntities[i].ageCategoryId !== person.ageInterval.age_category.id) {
        person.productEntities.splice(i, 1);
      }
    }
    this.updateCart(CartEventType.personAgeUpdated);
  }

  /**
   * It will update the name of the person at given index
   */
  public updatePersonName(name: string | undefined, index: number) {
    this.cart.persons[index].name = name?.trim();
    this.updateCart(CartEventType.personNameUpdated);
  }

  /**
   * It will remove the person at given index.
   * If needed, will also update the selected person
   */
  public removePerson(index: number) {
    this.cart.persons.splice(index, 1);

    if (this.cart.persons.indexOf(this.cart.selectedPerson) === -1) {
      if (index === 0) {
        this.cart.selectedPerson = this.cart.persons[0];
      } else if (index === this.cart.persons.length) {
        this.cart.selectedPerson = this.cart.persons[this.cart.persons.length - 1];
      } else {
        this.cart.selectedPerson = this.cart.persons[index];
      }
    }
    this.updateCart(CartEventType.personRemoved);
  }

  /**
   * It will add a product to the person at given index
   */
  public updateProduct(person: Person, product: Product) {
    const prod = new Product(product);
    // find out if product is already in person and therefore needs replacement
    let replaceProduct = person.productEntities.find(p => {
      return (
        p.definitionId === product.definitionId || // same product -> update to addons
        p.productCategoryId === product.productCategoryId // category is the same and..
      );
    });
    // replace the item since it is updated
    if (replaceProduct) {
      replaceProduct = person.productEntities.splice(
        person.productEntities.findIndex(pE => pE.definitionId === replaceProduct.definitionId),
        1,
        prod
      )[0];
    } else {
      // add a new one
      person.productEntities.push(prod);
    }
    this.updateCart(CartEventType.productUpdated, person, product, replaceProduct);
  }

  /**
   * It will remove a product to the person at given index
   */
  public removeProduct(person: Person, productId: number) {
    const productIndex = person.productEntities.findIndex(p => p.definitionId === productId);
    if (productIndex > -1) {
      const product = person.productEntities.splice(productIndex, 1);
      this.updateCart(CartEventType.productRemoved, person, product[0]);
    }
  }

  /**
   * sets a new rental date range and will emit an xxx event
   * @param range
   */
  public updateDateRange(range: { start: Date; end: Date }) {
    this.bookingParameterService.bookingParameters.start = range.start;
    this.bookingParameterService.bookingParameters.end = range.end;
    this.bookingParameterService.saveBookingParameters(this.bookingParameterService.bookingParameters);
    this.cart.startDate = range.start;
    this.cart.endDate = range.end;
    this.updateCart(CartEventType.dateRangeUpdated);
  }

  /**
   * set or remove ('') promocode from cart
   * @param code the promotionCode to set
   */
  public setPromocode(code: string) {
    this.cart.promotionCode = code;
    this.bookingParameterService.bookingParameters.setActioncode({ type: codetype.promo, text: code });
    this.updateCart(CartEventType.promocodeUpdated);
  }

  public setCouponcode(code: string) {
    this.cart.couponCode = code;
    this.bookingParameterService.bookingParameters.setActioncode({ type: codetype.coupon, text: code });
    this.updateCart(CartEventType.couponcodeUpdated);
  }

  /**
   * Returns true if personAges in params is a correct array of number-values,
   * false if not.
   */
  private isPersonAgesValid(params: ParamMap): boolean {
    if (!params.has('personAges[]')) {
      return false;
    }
    const personAges = params.getAll('personAges[]');
    if (!Array.isArray(personAges)) {
      return false;
    }
    for (let i = 0; i < personAges.length; ++i) {
      if (!Number(personAges[i])) {
        return false;
      }
    }
    return true;
  }

  /**
   * this function sanitizes the given params and will patch/replace
   * missing or broken ones from bookingParameters
   * @param params ParamMap containing all necessary cart parametes like startDate, persons, etc.
   */
  private sanitizeParams(params: ParamMap, shop: Shop, shopProducts: Product[]): CartInterface {
    let cart: CartInterface = { persons: [] };
    if (params.has('cart')) {
      // parse and use cart data if applicable
      cart = JSON.parse(params.get('cart'));
    }

    // no people in cart, get them
    if (!cart.persons.length) {
      // use personAges[] if applicable
      if (this.isPersonAgesValid(params)) {
        cart.persons = params.getAll('personAges[]').map(age => ({
          age: +age,
          products: []
        }));
      } else {
        // fallback to default 'adult' in shop
        const defaultAI = shop.ageIntervals.find(aI => aI.is_default);
        cart.persons.push({ age: defaultAI ? defaultAI.start : this.defaultAgeAdult, products: [] });
      }
    }

    // check persons' ages and products
    else {
      cart.persons.forEach(person => {
        if (!Number(person.age) || person.age < 1 || person.age > 99) {
          person.age = this.defaultAgeAdult;
        }

        // remove product from cart if is unavailable in shop
        person.products = person.products.filter(product =>
          shopProducts.some(p => p.definitionId === product.definitionId)
        );

        // remove unavailable addons from products
        person.products.forEach(product => {
          const shopProduct = shopProducts.find(p => p.definitionId === product.definitionId);
          product.addons.filter(addon => shopProduct.addons.some(a => +a.definitionId === addon));
        });
      });
    }
    cart.startDate = params.get('startDate');
    cart.endDate = params.get('endDate');
    this.validateAndAdjustCartDates(cart);

    // incomplete or no date range, use default
    if (!cart.startDate || !cart.endDate) {
      cart.startDate = this.dateToString(this.bookingParameterService.bookingParameters.start);
      cart.endDate = this.dateToString(this.bookingParameterService.bookingParameters.end);
    }
    this.bookingParameterService.bookingParameters.start = new Date(cart.startDate);
    this.bookingParameterService.bookingParameters.end = new Date(cart.endDate);

    if (cart.couponCode) {
      this.bookingParameterService.bookingParameters.setActioncode({
        type: codetype.coupon,
        text: cart.couponCode
      });
    }
    if (cart.promotionCode) {
      this.bookingParameterService.bookingParameters.setActioncode({
        type: codetype.promo,
        text: cart.promotionCode
      });
    }
    // update the booking params
    this.bookingParameterService.saveBookingParameters(this.bookingParameterService.bookingParameters);

    return cart;
  }

  private validateAndAdjustCartDates(cart: CartInterface) {
    // Convert the startDate and endDate from strings to Date objects
    const start = new Date(cart.startDate);
    const end = new Date(cart.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start < today || end < start) {
      const bookingParams = this.bookingParameterService.createNewBookingParameters();

      cart.startDate = this.dateToString(bookingParams.start);
      cart.endDate = this.dateToString(bookingParams.end);
      return;
    }

    // Check if the date range is more than 14 days
    const dayDifference = (end.getTime() - start.getTime()) / (1000 * 3600 * 24);
    if (dayDifference > 14) {
      // keep the start date and make the end date=start date +14 days
      cart.endDate = this.dateToString(new Date(start.getTime() + 14 * 24 * 60 * 60 * 1000));
    }
  }

  private dateToString(date: Date) {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  }

  filterProductsOnStop(persons: Person[]) {
    if (persons.some(pers => pers.productEntities.some(p => p.hasStopProduct))) {
      persons.forEach(pers => {
        pers.productEntities.filter(p => !p.hasStopProduct);
      });
    }
  }

  private fetchDiscountsFromCore(
    locale: Locale = Locale.en,
    currency = SupportedCurrency.EUR
  ): Observable<Cart> {
    const request: LegacyDynamicPricesRequestDto = {
      shopId: this.cart.shopId,
      locale,
      customerCountry: undefined,
      currency,
      rentalPeriod: {
        from: this.cart.startDateString,
        to: this.cart.endDateString
      },
      person: this.getPersonsFromCart(),
      coupons: {},
      promoCodes: {},
      insurance: this.getInsurancesFromCart() as unknown as { [key: string]: LegacyInsuranceDto },
      partnerPrefix: this.platformService.getCurrentPartner()
    };

    if (this.cart.couponCode) {
      request.coupons = { 1: { code: this.cart.couponCode } };
    }
    if (this.cart.promotionCode) {
      request.promoCodes = { 1: { code: this.cart.promotionCode } };
    }

    // remove all the discounts to enable the loading spinners
    this.mapDiscountsToPersons();

    return this.platformInformationService.customerCountryCode$.pipe(
      // make sure to only call discounts when you know the customers country
      switchMap(resp => {
        const country = this.cookieService.get('cart_customer_country') || resp;
        request.customerCountry = country as Locale;
        return this.coreService
          .apiCorePaymentDynamicPriceInfo(request, undefined, false, {
            responseType: 'json',
            headers: new HttpHeaders().set('Cache-Control', 'no-cache')
          })
          .pipe(
            delayedRetry(100, 3),
            map(discounts => {
              this.mapDiscountsToPersons(discounts);
              return this.cart;
            }),
            catchError(() => {
              return of(null);
            })
          );
      })
    );
  }

  private convertRates(rates: { [key: string]: string | number }): { [key: string]: number } {
    const convertedRates: { [key: string]: number } = {};

    for (const key in rates) {
      let value = rates[key];
      if (typeof value !== 'number') {
        value = parseFloat(value);
        if (isNaN(value)) {
          throw new Error(`Invalid number for key: ${key}`);
        }
      }
      convertedRates[key] = value;
    }
    return convertedRates;
  }

  private mapDiscountsToPersons(discounts?: LegacyDynamicPricesResponseDto) {
    if (discounts) {
      this.cart.averageQuality = discounts.averageQuality;
      this.cart.fee = discounts.fee;
      this.cart.promotionCodeReduction = discounts.promotionCodeReduction;
      this.cart.totalPaidOnlineBothTypes = discounts.totalPaidOnlineBothTypes;
      this.cart.totalPaidCash = discounts.totalPaidCash;
      this.cart.totalPaidOnlineAfterCoupons = discounts.totalPaidOnlineAfterCoupons;
      this.cart.discountAbsolute = discounts.discountAbsolute;
      this.cart.totalDeductedFromCoupons = discounts.totalDeductedFromCoupons;
      this.cart.discountPercentage = discounts.discountPercentage;
      this.cart.paymentTypeChoiceAvailable = discounts.paymentTypeChoiceAvailable;

      // protection/global item prices
      this.cart.isGuarantyBookable = discounts.isGuarantyBookable;
      this.cart.useGuarantyInterface = discounts.useGuarantyInterface;
      this.cart.potentialCancellationInsuranceRaw =
        discounts.potentialCancellationInsuranceRaw as unknown as AmountInterface;
      this.cart.potentialGuarantyRaw = discounts.potentialGuarantyRaw as unknown as AmountInterface;
      this.cart.potentialInsuranceRaw = discounts.potentialInsuranceRaw as unknown as AmountInterface;
      this.cart.potentialInsurancePlusRaw = discounts.potentialInsurancePlusRaw as unknown as AmountInterface;

      if (discounts.euroBasedRates) {
        this.cart.euroBasedRates = {
          rates: this.convertRates(discounts.euroBasedRates),
          code: 'EUR'
        };
      }
    }
    this.cart.persons.forEach((person, index) => {
      person.discounts = discounts?.priceInfo[index];
    });
    this.updateCart(CartEventType.discountsUpdated);
  }

  private getInsurancesFromCart(): { definitionId: number }[] {
    return [
      this.cart.cancellationSelected,
      this.cart.insuranceSelected,
      this.cart.insurancePlusSelected
    ].reduce((acc, value, index) => {
      if (value) acc.push({ definitionId: index + 1 });
      return acc;
    }, []);
  }

  private getPersonsFromCart(): { [key: string]: LegacyPersonDto | undefined } {
    const result = this.cart.persons.reduce((acc, person, index) => {
      acc[index] = {
        age: person.age,
        equipment: this.getEquipmentsFromProducts(person.productEntities)
      };
      return acc;
    }, {});
    return result;
  }

  private getEquipmentsFromProducts(products: Product[]): { [key: string]: LegacyEquipmentDto | undefined } {
    const result = products.reduce((acc, product, index) => {
      const product_: LegacyEquipmentDto = {
        definitionId: product.definitionId,
        accessories: this.getProductAccessories(product.addons)
      };
      acc[index] = product_;
      return acc;
    }, {});

    return result;
  }

  private getProductAccessories(addons: Addon[]): { [key: string]: LegacyAccessoryDto } {
    const result = addons.reduce((acc, addon, index) => {
      const addon_: LegacyAccessoryDto = {
        definitionId: addon.definitionId
      };
      acc[index] = addon_;
      return acc;
    }, {});
    return result;
  }

  public updateCart(eventType: CartEventType, person?: Person, product?: Product, replacedProduct?: Product) {
    const evt = { cart: this.cart, eventType, person, product, replacedProduct };
    if (!person) {
      delete evt.person;
    }
    if (!product) {
      delete evt.product;
    }
    if (!replacedProduct) {
      delete evt.replacedProduct;
    }
    this.cart$.next(evt);
  }
}
